import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supplierPortalPublicService, DepotFacture } from '../../api/portal';
import { SupplierContext, SupplierOrder } from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

/**
 * Portail fournisseur (TF-052 → TF-054) — page PUBLIQUE, hors coquille authentifiée.
 * Le fournisseur consulte ses bons de commande, confirme un délai de livraison, et renseigne
 * ses données carbone (transport amont) — qui alimentent le score fournisseur et le bilan carbone.
 */
export function PortailFournisseur() {
  const { token = '' } = useParams<{ token: string }>();
  const [ctx, setCtx] = useState<SupplierContext | null>(null);
  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [co2, setCo2] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // TF-055 — dépôt de facture
  const [depots, setDepots] = useState<DepotFacture[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      supplierPortalPublicService.getContext(token).then((c) => {
        setCtx(c);
        setCo2(c.transportCo2ParCommande != null ? String(c.transportCo2ParCommande) : '');
      }),
      supplierPortalPublicService.getCommandes(token).then(setOrders),
      supplierPortalPublicService.mesDepots(token).then(setDepots).catch(() => setDepots([])),
    ]).catch(() => setError('Lien invalide ou expiré.')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const confirmer = async (id: number) => {
    const v = window.prompt('Délai de livraison confirmé (en jours) ?');
    if (v == null || v === '') return;
    await supplierPortalPublicService.confirmDelai(token, id, Number(v));
    load();
  };
  const saveCarbon = async () => {
    if (co2 === '') return;
    await supplierPortalPublicService.updateCarbon(token, Number(co2));
    load();
  };

  const deposer = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setUploadMsg(null);
    try {
      await supplierPortalPublicService.deposerFacture(token, file);
      setUploadMsg('Facture déposée — elle sera traitée par votre client.');
      setDepots(await supplierPortalPublicService.mesDepots(token));
    } catch (err: any) {
      setUploadMsg(err?.response?.data?.message || 'Dépôt impossible (PDF ou image, 10 Mo max).');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>;
  if (error || !ctx) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="card text-center max-w-md">
          <p className="text-lg font-semibold text-gray-800">Accès indisponible</p>
          <p className="text-gray-500 mt-2">{error ?? 'Lien invalide.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Espace fournisseur</p>
          <h1 className="text-xl font-bold text-gray-900">{ctx.societe}</h1>
          <p className="text-sm text-gray-500">
            {ctx.fournisseur}{ctx.noteQualite != null ? ` · note qualité ${ctx.noteQualite}/5` : ''}
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Données carbone */}
        <div className="card">
          <h2 className="font-semibold mb-1">Vos données carbone</h2>
          <p className="text-xs text-gray-500 mb-3">
            Empreinte du transport amont par commande (kg CO₂e) — utilisée pour le bilan carbone partagé.
          </p>
          <div className="flex items-end gap-2">
            <input className="input-field w-40" type="number" step="0.001" value={co2}
              onChange={(e) => setCo2(e.target.value)} placeholder="ex. 12.500" />
            <button onClick={saveCarbon} className="btn-primary">Enregistrer</button>
          </div>
        </div>

        {/* TF-055 — dépôt de facture */}
        <div className="card">
          <h2 className="font-semibold mb-1">Déposer une facture</h2>
          <p className="text-xs text-gray-500 mb-3">
            PDF ou image (10 Mo max). Les montants sont pré-lus automatiquement, puis relus par votre client.
          </p>
          <label className={`btn-primary inline-block cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            {uploading ? 'Envoi…' : 'Choisir un fichier'}
            <input type="file" accept="application/pdf,image/*" className="hidden" onChange={deposer} />
          </label>
          {uploadMsg && <p className="text-xs text-gray-600 mt-2">{uploadMsg}</p>}
          {depots.length > 0 && (
            <table className="min-w-full text-sm mt-4">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-3 py-1.5">Fichier</th>
                  <th className="px-3 py-1.5">Déposée le</th>
                  <th className="px-3 py-1.5 text-right">Total TTC lu</th>
                  <th className="px-3 py-1.5">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {depots.map((d) => (
                  <tr key={d.id}>
                    <td className="px-3 py-1.5">{d.nomFichier}</td>
                    <td className="px-3 py-1.5 text-gray-500">{new Date(d.createdAt).toLocaleDateString()}</td>
                    <td className="px-3 py-1.5 text-right">
                      {d.totalTTC != null ? <MoneyDisplay amount={d.totalTTC} size="sm" /> : '—'}
                    </td>
                    <td className="px-3 py-1.5">
                      <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${
                        d.statut === 'TRAITE' ? 'bg-green-100 text-green-800'
                          : d.statut === 'REJETE' ? 'bg-red-100 text-red-800'
                          : 'bg-amber-100 text-amber-800'}`}>
                        {d.statut === 'TRAITE' ? 'Traitée' : d.statut === 'REJETE' ? 'Rejetée' : 'Reçue'}
                      </span>
                      {d.note && <span className="ml-2 text-xs text-gray-400">{d.note}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Bons de commande */}
        <h2 className="font-semibold text-gray-700">Bons de commande ({orders.length})</h2>
        {orders.length === 0 ? (
          <div className="card text-center text-gray-500">Aucune commande pour le moment.</div>
        ) : orders.map((o) => (
          <div key={o.id} className="card">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-gray-900">Commande {o.numero}</p>
                <p className="text-xs text-gray-500">{o.dateDocument}</p>
                {o.delaiConfirmeJours != null &&
                  <p className="text-xs text-green-600 mt-1">Délai confirmé : {o.delaiConfirmeJours} j</p>}
              </div>
              <div className="text-right">
                <MoneyDisplay amount={o.totalTTC} size="lg" className="font-bold" />
                <div className="mt-1">
                  <button onClick={() => confirmer(o.id)} className="btn-secondary text-sm">
                    {o.delaiConfirmeJours != null ? 'Modifier le délai' : 'Confirmer le délai'}
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                    <th className="px-3 py-1.5">Article</th>
                    <th className="px-3 py-1.5 text-right">Qté</th>
                    <th className="px-3 py-1.5 text-right">P.U.</th>
                    <th className="px-3 py-1.5 text-right">Montant HT</th>
                    <th className="px-3 py-1.5 text-right">Facteur CO₂</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {o.lignes.map((l, i) => (
                    <tr key={i}>
                      <td className="px-3 py-1.5">{l.designation}</td>
                      <td className="px-3 py-1.5 text-right">{l.quantite}</td>
                      <td className="px-3 py-1.5 text-right"><MoneyDisplay amount={l.prixUnitaire} size="sm" /></td>
                      <td className="px-3 py-1.5 text-right"><MoneyDisplay amount={l.montantHT} size="sm" /></td>
                      <td className="px-3 py-1.5 text-right text-gray-500">
                        {l.emissionFactor != null ? `${l.emissionFactor} kg/u` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        <p className="text-center text-xs text-gray-400 pt-4">Propulsé par TuniFact</p>
      </main>
    </div>
  );
}

export default PortailFournisseur;
